import axios from "axios";
import { getErrorRes, getSuccessRes } from "../helpers/api-response";
import { handleErrorMessage } from "../helpers/handle-error-message";
import { API_BASE_URL } from "../config/api-config";
import axiosIns from "../common/interceptor/axios-interceptor";
import logger from "../helpers/logger";

// Helper function to retry failed requests
const retryRequest = async (apiCall, maxRetries = 2, delay = 1000) => {
  let retries = 0;
  let lastError = null;

  while (retries < maxRetries) {
    try {
      return await apiCall();
    } catch (error) {
      lastError = error;
      retries++;
      logger.log(`Attempt ${retries} failed. Retrying in ${delay}ms...`);
      const currentDelay = delay; // Capture current delay value
      await new Promise((resolve) => setTimeout(resolve, currentDelay));
      // Increase delay for next retry (exponential backoff)
      delay *= 2;
    }
  }

  // If we get here, all retries failed
  throw lastError;
};

export const fetchRackPricingData = async (
  date,
  noCache = false,
  toastId = false
) => {
  try {
    logger.log(
      `Fetching rack pricing data for ${date} from rack-pricing/rackpricing`
    );

    // Try the diagnostics endpoint first if there's an issue
    if (noCache) {
      try {
        logger.log("Running API diagnostics check first...");
        const diagnosticsRes = await axiosIns.get(
          `rack-pricing/diagnostics`
        );
        logger.log("API Diagnostics:", diagnosticsRes.data);
      } catch (diagError) {
        logger.warn(
          "Diagnostics endpoint failed, continuing with main request:",
          diagError
        );
      }
    }

    // Use retry logic for the main request
    const apiCall = () =>
      axiosIns.get(`rack-pricing/rackpricing`, {
        params: { date, noCache },
      });

    const res = await retryRequest(apiCall);

    return getSuccessRes(
      res.data || [],
      "Rack Pricing data fetched successfully."
    );
  } catch (error) {
    logger.error("Error fetching rack pricing data:", error);
    logger.error("Error details:", {
      message: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
    });

    // Handle 404 responses that indicate "no data available" as successful empty results
    if (
      error.response?.status === 404 &&
      error.response?.data &&
      typeof error.response.data === "string" &&
      error.response.data.includes("No RackPricing data available")
    ) {
      logger.log(
        "No data available for selected date - returning empty result"
      );
      return getSuccessRes([], error.response.data);
    }

    let errMessage = "";
    if (toastId) {
      errMessage = handleErrorMessage(error, true, toastId);
    } else {
      errMessage = handleErrorMessage(error, true);
    }
    throw getErrorRes(errMessage, `Failed to fetch Rack Pricing data.`);
  }
};

export const updateRackPricingStoredProc = async (inputDate) => {
  try {
    // Convert string date back to Date object for the backend
    const dateObj = new Date(inputDate + "T00:00:00.000Z");

    // Detailed logging for debugging
    logger.log("🔍 updateRackPricingStoredProc - Debug Info:");
    logger.log("  - Original inputDate:", inputDate);
    logger.log("  - Converted dateObj:", dateObj);
    logger.log("  - dateObj.toISOString():", dateObj.toISOString());
    logger.log("  - API endpoint:", `pricing/rack`);

    const requestPayload = { InputDate: dateObj };
    logger.log(
      "  - Request payload:",
      JSON.stringify(requestPayload, null, 2)
    );

    const res = await axiosIns.post(
      `pricing/rack`,
      requestPayload,
      {
        headers: { "Content-Type": "application/json" },
      }
    );

    logger.log("✅ updateRackPricingStoredProc - Success Response:");
    logger.log("  - Status:", res.status);
    logger.log("  - Response data:", res.data);

    return getSuccessRes(res.data, "Rack pricing updated successfully.");
  } catch (error) {
    logger.error("❌ updateRackPricingStoredProc - Error Details:");
    logger.error("  - Error message:", error.message);
    logger.error("  - Error status:", error.response?.status);
    logger.error("  - Error status text:", error.response?.statusText);
    logger.error("  - Error response data:", error.response?.data);
    logger.error("  - Error response headers:", error.response?.headers);
    logger.error("  - Request config:", error.config);
    logger.error("  - Full error object:", error);

    const errMessage = handleErrorMessage(error, true);
    throw getErrorRes(errMessage, "Failed to update rack pricing.");
  }
};

export const updateRackPricing = async (dateParameters) => {
  try {
    const res = await axiosIns.post(
      `rack-pricing/updateRackPricing`,
      dateParameters,
      {
        headers: { "Content-Type": "application/json" },
      }
    );
    return getSuccessRes(res.data, "Rack pricing updated successfully.");
  } catch (error) {
    const errMessage = handleErrorMessage(error, true);
    throw getErrorRes(errMessage, "Failed to update rack pricing.");
  }
};

export const updateRackPricingByRow = async (dateParameters) => {
  try {
    const res = await axiosIns.post(
      `rack-pricing/updateRackPricingByRow`,
      dateParameters,
      {
        headers: { "Content-Type": "application/json" },
      }
    );
    return getSuccessRes(res.data, "Rack pricing updated successfully by Terminal Group.");
  } catch (error) {
    const errMessage = handleErrorMessage(error, true);
    throw getErrorRes(errMessage, "Failed to update rack pricing by row.");
  }
};

export const updateRackPricingByRowStoredProc = async (data) => {
  try {
    // Convert string date back to Date object for the backend
    const dateObj = new Date(data.inputDate + "T00:00:00.000Z");

    const res = await axiosIns.post(
      `pricing/rack/byrow`,
      {
        InputDate: dateObj,
        InputRPKey: data.inputRPKey,
        InputTerminalCity: data.inputTerminalCity,
        InputDTNProductID: data.inputDTNProductID,
      },
      {
        headers: { "Content-Type": "application/json" },
      }
    );
    return getSuccessRes(res.data, "Rack pricing updated successfully by Terminal Group.");
  } catch (error) {
    const errMessage = handleErrorMessage(error, true);
    throw getErrorRes(errMessage, "Failed to update rack pricing by row.");
  }
};

export const updateRackPricingData = async (payload) => {
  try {
    const res = await axiosIns.post(
      `rack-pricing/updateRackPricing`,
      payload,
      {
        headers: { "Content-Type": "application/json" },
      }
    );
    return getSuccessRes(res.data, "Rack pricing updated successfully.");
  } catch (error) {
    // Check if this is a conflict error (HTTP 409)
    if (error.response && error.response.status === 409) {
      const conflictData = error.response.data;
      const conflictError = new Error(
        conflictData.message ||
        "Data has been modified by another user. Please refresh and try again."
      );
      conflictError.isSuccess = false;
      conflictError.data = conflictData;
      conflictError.conflict = true;
      conflictError.conflictingRows = conflictData.conflictingRows || [];
      throw conflictError;
    }

    // Handle other errors
    const errMessage = handleErrorMessage(error, true);
    throw getErrorRes(errMessage, "Failed to update rack pricing.");
  }
};

export const updateRackPricingByTGStoredProc = async (data) => {
  try {
    // Convert string date back to Date object for the backend
    const dateObj = new Date(data.inputDate + "T00:00:00.000Z");

    const res = await axiosIns.post(
      `pricing/rack/byterminalgroup`,
      {
        InputDate: dateObj,
        TerminalGroupKey: data.terminalGroupKey
      },
      {
        headers: { "Content-Type": "application/json" },
      }
    );
    return getSuccessRes(res.data, "Rack pricing updated successfully by terminal group.");
  } catch (error) {
    const errMessage = handleErrorMessage(error, true);
    throw getErrorRes(errMessage, "Failed to update rack pricing by terminal group.");
  }
};
